/**
 * O **Nome** com sugestão das linhas da ficha nova (13.19c1, chamado de 26/10/2026, item C).
 *
 * "Ao adicionar pessoa, tem que trazer as pessoas já cadastradas conforme for digitando, assim como
 * em pessoas tem empresas": o campo Nome da linha sugere as Pessoas que quem cadastra vê **pela
 * base** (`GET /pessoas/sugestoes`: as das Empresas de que é Responsável, as soltas que recebeu e,
 * para a gestão, todas — nunca a vista só pelo Grupo, pela PA-1 ou pela PA-3; quem recorta é o
 * serviço). Sem sugestão escolhida, o texto é o Nome da Pessoa nova de hoje.
 *
 * **O molde é o campo de Empresa** (`CampoDeEmpresa.tsx`): combobox ARIA com região viva da
 * contagem, 300 ms de digitação parada, termo aparado e cortado em `TAMANHO_DA_BUSCA`, de 10 em 10
 * com "Mostrar mais", o resultado velho `aria-busy` e não escolhível, ↑ ↓ Esc (com a lista aberta,
 * o Esc faz `preventDefault`), o `mousedown` das opções prevenido. A diferença é que não há "Criar
 * nova": a linha já é a Pessoa nova. Por isso a busca sem nenhuma Pessoa **não abre lista** (nada
 * cobre o Cargo nem o "+ Adicionar pessoa" de quem cadastra alguém novo), e **o Enter só é da lista
 * com uma opção escolhível ativa** — sem ela (nenhuma marcada, a lista desatualizada, só a falha),
 * ele fecha a lista e segue para a ficha, como em qualquer campo dela (code review da 13.19c1).
 *
 * **A lista abre por cima e nunca é cortada**: em portal, `position: fixed` na camada
 * `--camada-painel-flutuante` (acima de todo véu de modal), posicionada por `usePosicaoDoPainel` —
 * a peça do painel das etiquetas (13.15) que o campo "Grupo" da ficha nova já usa (13.18) — com a
 * largura real dela (a mínima do token). O portal é o diálogo que contém o campo, quando há um (o
 * modal do "+ Empresa" no Novo negócio), e o `document.body` fora dele (`conteinerDoPainel`). O
 * cartão Pessoas tem `overflow: hidden`, e a lista dentro dele sairia cortada na borda.
 *
 * **Leitor de tela:** a região viva da contagem é permanente no campo (fora da lista, como no
 * campo de Empresa); a falha tem `id` e entra no `aria-describedby`; o `aria-expanded` é verdadeiro
 * sempre que a lista está à vista, também só com a falha. O desvio do Spec Change Log da spec.
 *
 * Cada sugestão mostra o nome e, abaixo, o telefone formatado, o cargo e a Empresa — a primeira,
 * pelo nome exibido, com "+N" — ou "Sem empresa". A consulta só sai com o campo em foco: as linhas
 * já digitadas não perguntam nada a cada nova renderização.
 */

import { type KeyboardEvent, useEffect, useId, useRef, useState } from 'react'
import { createPortal } from 'react-dom'

import { ErroDaApi } from '@/api/erros'
import { LinhaDoRotulo } from '@/shell/MarcaDeObrigatorio'
import { type PessoaSugerida, TAMANHO_DA_BUSCA, useSugestoesDePessoas } from './api'
import { CARREGANDO_MAIS, MOSTRAR_MAIS, TENTAR_MAIS_DE_NOVO } from './CampoDeEmpresa'
import { conteinerDoPainel, usePosicaoDoPainel } from './etiquetas'
import { nomeExibidoDaEmpresa } from './nome-da-empresa'
import { telefoneNaTela } from './telefone-na-tela'
import './campo-de-empresa.css'

/** Quanto a digitação precisa parar antes de a busca sair — o mesmo do campo de Empresa. */
export const ESPERA_DA_SUGESTAO_EM_MS = 300

/** O nome acessível da lista. */
export const ROTULO_DA_LISTA_DE_PESSOAS = 'Pessoas cadastradas'

/** A Pessoa sem vínculo nenhum (a solta), na opção e na linha escolhida. */
export const SEM_EMPRESA = 'Sem empresa'

/** Falha de rede (ou resposta sem envelope): o texto é do SPA, porque o serviço não falou. */
export const AVISO_DA_SUGESTAO_INDISPONIVEL =
  'Não foi possível buscar as pessoas cadastradas agora. A linha cadastra uma nova.'

/** A mesma Pessoa escolhida em duas linhas (13.19c1, recomendação do coordenador). */
export const PESSOA_JA_NA_LISTA = 'Esta pessoa já está na lista.'

/**
 * Duas Pessoas escolhidas, diferentes, com o mesmo telefone (code review da 13.19c1): a segunda é
 * recusada na tela, com o texto que o serviço dá à mesma recusa.
 */
export const PESSOA_COM_TELEFONE_REPETIDO_NA_LISTA =
  'Esta pessoa tem o mesmo telefone de outra pessoa da lista.'

const BUSCANDO = 'Buscando…'

function anuncioDe(quantas: number): string {
  if (quantas === 0) {
    return 'Nenhuma pessoa encontrada'
  }
  return quantas === 1 ? '1 pessoa encontrada' : `${String(quantas)} pessoas encontradas`
}

/** A Empresa da Pessoa na opção e na linha escolhida: a primeira e "+N", ou "Sem empresa". */
export function empresaNaSugestao(pessoa: PessoaSugerida): string {
  const [primeira, ...outras] = pessoa.empresas
  if (primeira === undefined) {
    return SEM_EMPRESA
  }
  const nome = nomeExibidoDaEmpresa(primeira)
  return outras.length === 0 ? nome : `${nome} +${String(outras.length)}`
}

/**
 * O aviso da linha escolhida (13.19c1): a primeira Empresa dela vista pela base — sem contagem e
 * sem gênero. A solta não tem aviso (`null`).
 */
export function avisoDaPessoaEscolhida(pessoa: PessoaSugerida): string | null {
  const primeira = pessoa.empresas[0]
  if (primeira === undefined) {
    return null
  }
  return `${pessoa.nome} já é contato da Empresa ${nomeExibidoDaEmpresa(primeira)} e passa a ser também desta.`
}

/** O termo como vai à rede: aparado e cortado no teto do serviço. */
function termoDe(texto: string): string {
  return texto.trim().slice(0, TAMANHO_DA_BUSCA)
}

function mensagemDaFalha(falha: unknown): string {
  if (falha instanceof ErroDaApi && falha.codigo !== 'erro_desconhecido') {
    return falha.message
  }
  return AVISO_DA_SUGESTAO_INDISPONIVEL
}

type Opcao = { tipo: 'pessoa'; pessoa: PessoaSugerida } | { tipo: 'mais' }

export interface PropriedadesDoCampoDoNome {
  /** O `id` do `<input>` (`pessoa-nome-<sufixo>`) — o `<label>` se associa por ele. */
  id: string
  valor: string
  aoTrocar: (valor: string) => void
  /** A Pessoa escolhida na lista: quem monta a liga à linha. */
  aoEscolher: (pessoa: PessoaSugerida) => void
  maxLength: number
  autoComplete?: string
  /** O `id` do `<form>` da ficha (a linha mora fora dele). */
  formulario?: string
  /** `id` da mensagem que recusou o Nome, quando há uma. */
  recusadoPor?: string | null
  desabilitado: boolean
}

export function CampoDoNomeComSugestao({
  id,
  valor,
  aoTrocar,
  aoEscolher,
  maxLength,
  autoComplete,
  formulario,
  recusadoPor,
  desabilitado,
}: PropriedadesDoCampoDoNome) {
  const base = useId()
  const idDaLista = `${base}-lista`
  const [emFoco, setEmFoco] = useState(false)
  const [aberta, setAberta] = useState(false)
  /* A opção ativa vale só para a lista em que foi marcada (o molde do campo de Empresa). */
  const [marcada, setMarcada] = useState<{ chave: string; indice: number }>({
    chave: '',
    indice: -1,
  })
  const [termo, setTermo] = useState('')
  const entrada = useRef<HTMLInputElement>(null)
  const popup = useRef<HTMLDivElement>(null)
  const lista = useRef<HTMLUListElement>(null)
  const idDaFalha = `${base}-falha`
  /* Onde a lista mora: o diálogo do campo, quando há um (code review da 13.19c1). */
  const [conteiner, setConteiner] = useState<HTMLElement | null>(null)

  const digitado = termoDe(valor)
  useEffect(() => {
    const espera = setTimeout(() => {
      setTermo(digitado)
    }, ESPERA_DA_SUGESTAO_EM_MS)
    return () => {
      clearTimeout(espera)
    }
  }, [digitado])

  const consulta = useSugestoesDePessoas(termo, {
    habilitada: termo !== '' && emFoco && !desabilitado,
  })
  const chaveDaLista = `${termo}|${String(consulta.dataUpdatedAt)}`
  const ativa = marcada.chave === chaveDaLista ? marcada.indice : -1
  const setAtiva = (indice: number) => {
    setMarcada({ chave: chaveDaLista, indice })
  }

  const comTermo = termo !== '' && digitado !== ''
  const falhou = comTermo && consulta.isError && !consulta.isFetchNextPageError
  const paginas = comTermo && !falhou ? (consulta.data?.pages ?? []) : []
  /* Sem repetir: com escrita entre uma página e outra, o `OFFSET` pode devolver a mesma Pessoa. */
  const sugestoes = [
    ...new Map(paginas.flatMap((pagina) => pagina.itens).map((item) => [item.id, item])).values(),
  ]
  const total = paginas.at(-1)?.total ?? 0
  const temMais = comTermo && !falhou && consulta.hasNextPage
  const desatualizada = comTermo && (consulta.isPlaceholderData || digitado !== termo)
  const buscando = digitado !== '' && (digitado !== termo || consulta.isFetching)

  const opcoes: Opcao[] = [
    ...sugestoes.map((pessoa) => ({ tipo: 'pessoa' as const, pessoa })),
    ...(temMais ? [{ tipo: 'mais' as const }] : []),
  ]
  const escolhivel = (opcao: Opcao) =>
    opcao.tipo === 'mais' ? !desatualizada && !consulta.isFetchingNextPage : !desatualizada
  /* A lista só abre com o que mostrar: as sugestões ou a falha. Sem nenhuma Pessoa, não abre — a
     linha é a Pessoa nova, e uma nota por cima cobriria os campos de quem cadastra alguém novo. */
  const temOQueMostrar = opcoes.length > 0 || falhou
  const listaAberta = aberta && emFoco && !desabilitado && digitado !== '' && temOQueMostrar
  const comOpcoes = listaAberta && opcoes.length > 0
  const posicao = usePosicaoDoPainel(listaAberta, entrada, popup, { daLarguraDaAncora: true })

  useEffect(() => {
    if (!listaAberta || ativa < 0) {
      return
    }
    // `scrollIntoView` não existe no jsdom: no celular é ela que mantém a ativa visível.
    lista.current?.querySelector<HTMLElement>(`[data-indice="${String(ativa)}"]`)?.scrollIntoView?.({
      block: 'nearest',
    })
  }, [ativa, listaAberta])

  function escolher(opcao: Opcao) {
    if (!escolhivel(opcao)) {
      return
    }
    if (opcao.tipo === 'mais') {
      const primeiraNova = sugestoes.length
      void consulta.fetchNextPage().then((resultado) => {
        if (!resultado.isSuccess) {
          return
        }
        const carregadas = new Set(
          resultado.data.pages.flatMap((pagina) => pagina.itens).map((item) => item.id),
        ).size
        setMarcada({
          chave: `${termo}|${String(resultado.dataUpdatedAt)}`,
          indice: carregadas > primeiraNova ? primeiraNova : Math.max(primeiraNova - 1, 0),
        })
      })
      return
    }
    setAberta(false)
    setAtiva(-1)
    aoEscolher(opcao.pessoa)
  }

  function mover(passo: 1 | -1) {
    const indices = opcoes.map((_, indice) => indice).filter((indice) => {
      const opcao = opcoes[indice]
      return opcao !== undefined && escolhivel(opcao)
    })
    if (indices.length === 0) {
      return
    }
    const atual = indices.indexOf(ativa)
    const proxima =
      atual === -1
        ? passo === 1
          ? 0
          : indices.length - 1
        : (atual + passo + indices.length) % indices.length
    setAtiva(indices[proxima] ?? -1)
  }

  function aoTeclar(evento: KeyboardEvent<HTMLInputElement>) {
    if (evento.key === 'ArrowDown' || evento.key === 'ArrowUp') {
      if (!comOpcoes) {
        if (!aberta && opcoes.length > 0) {
          evento.preventDefault()
          setAberta(true)
        }
        return
      }
      evento.preventDefault()
      mover(evento.key === 'ArrowDown' ? 1 : -1)
      return
    }
    if (!listaAberta) {
      return
    }
    if (evento.key === 'Enter') {
      const opcao = opcoes[ativa]
      if (comOpcoes && opcao !== undefined && escolhivel(opcao)) {
        // Com uma opção escolhível marcada, o Enter é da lista, e nunca da ficha.
        evento.preventDefault()
        escolher(opcao)
        return
      }
      // Sem ela (nenhuma marcada, a lista desatualizada, só a falha): a lista fecha, e o Enter
      // segue para a ficha, como em qualquer campo dela.
      setAberta(false)
      return
    }
    if (evento.key === 'Escape') {
      // Marcado: o modal da Empresa nova ouve `keydown` no `document` e respeita `defaultPrevented`.
      evento.preventDefault()
      setAberta(false)
      setAtiva(-1)
    }
  }

  const ativaNaLista = comOpcoes && ativa >= 0 && ativa < opcoes.length

  return (
    <div className="responsaveis__campo">
      <LinhaDoRotulo obrigatorio>
        <label className="responsaveis__rotulo" htmlFor={id}>
          Nome
        </label>
      </LinhaDoRotulo>
      <input
        ref={entrada}
        id={id}
        className="responsaveis__entrada"
        type="text"
        role="combobox"
        aria-autocomplete="list"
        aria-expanded={listaAberta}
        aria-controls={listaAberta ? idDaLista : undefined}
        aria-activedescendant={ativaNaLista ? `${base}-opcao-${String(ativa)}` : undefined}
        aria-invalid={recusadoPor ? true : undefined}
        aria-describedby={
          [recusadoPor ?? null, listaAberta && falhou ? idDaFalha : null]
            .filter((id): id is string => id !== null)
            .join(' ') || undefined
        }
        aria-required
        value={valor}
        autoComplete={autoComplete}
        form={formulario}
        maxLength={maxLength}
        disabled={desabilitado}
        onFocus={(evento) => {
          setConteiner(conteinerDoPainel(evento.currentTarget))
          setEmFoco(true)
          setAberta(true)
        }}
        onBlur={() => {
          setEmFoco(false)
          setAberta(false)
          setAtiva(-1)
        }}
        onKeyDown={aoTeclar}
        onChange={(evento) => {
          aoTrocar(evento.target.value)
          setAtiva(-1)
          setAberta(true)
        }}
      />
      {listaAberta
        ? createPortal(
            <div
              ref={popup}
              className="campo-de-empresa__popup campo-de-empresa__popup--flutuante campo-de-empresa__popup--da-pessoa"
              // Antes de medir, a lista existe sem aparecer: a primeira posição é a calculada.
              style={posicao ?? { top: 0, left: 0, visibility: 'hidden' }}
            >
              <ul
                ref={lista}
                id={idDaLista}
                hidden={opcoes.length === 0}
                className="campo-de-empresa__lista"
                role="listbox"
                aria-label={ROTULO_DA_LISTA_DE_PESSOAS}
                aria-busy={desatualizada ? true : undefined}
              >
                {opcoes.map((opcao, indice) => {
                  const desabilitada = !escolhivel(opcao)
                  const props = {
                    id: `${base}-opcao-${String(indice)}`,
                    role: 'option' as const,
                    'data-indice': indice,
                    'aria-selected': indice === ativa,
                    'aria-disabled': desabilitada ? true : undefined,
                    'data-ativa': indice === ativa ? 'sim' : undefined,
                    onMouseDown: (evento: { preventDefault: () => void }) => {
                      evento.preventDefault()
                    },
                    onPointerDown: (evento: { preventDefault: () => void }) => {
                      evento.preventDefault()
                    },
                    onClick: () => {
                      escolher(opcao)
                    },
                  }
                  if (opcao.tipo === 'mais') {
                    return (
                      <li
                        key="mais"
                        className="campo-de-empresa__opcao campo-de-empresa__mais"
                        {...props}
                      >
                        {consulta.isFetchingNextPage
                          ? CARREGANDO_MAIS
                          : consulta.isFetchNextPageError
                            ? TENTAR_MAIS_DE_NOVO
                            : MOSTRAR_MAIS}
                      </li>
                    )
                  }
                  const { pessoa } = opcao
                  const telefone =
                    pessoa.telefone === null || pessoa.telefone.trim() === ''
                      ? null
                      : telefoneNaTela(pessoa.telefone)
                  return (
                    <li key={pessoa.id} className="campo-de-empresa__opcao" {...props}>
                      <span className="campo-de-empresa__nome">{pessoa.nome}</span>
                      <span className="campo-de-empresa__detalhe">
                        {[telefone, pessoa.cargo, empresaNaSugestao(pessoa)]
                          .filter((parte): parte is string => parte !== null && parte !== '')
                          .join(' · ')}
                      </span>
                    </li>
                  )
                })}
              </ul>
              {total > sugestoes.length && !falhou ? (
                <p className="campo-de-empresa__nota">
                  {`${String(sugestoes.length)} de ${String(total)} encontradas`}
                </p>
              ) : null}
              {buscando && opcoes.length > 0 ? (
                <p className="campo-de-empresa__nota">{BUSCANDO}</p>
              ) : null}
              {falhou ? (
                <p className="campo-de-empresa__aviso campo-de-empresa__nota" id={idDaFalha}>
                  {mensagemDaFalha(consulta.error)}
                </p>
              ) : null}
            </div>,
            conteiner ?? document.body,
          )
        : null}
      {/* A região viva: permanente no campo e fora da lista, como no campo de Empresa — ela existe
          antes de o texto mudar, e é isso que faz o leitor de tela anunciá-lo. */}
      <span
        className="campo-de-empresa__so-para-leitor"
        role="status"
        data-testid="contagem-das-sugestoes"
      >
        {comOpcoes && !desatualizada && consulta.isSuccess
          ? total > sugestoes.length
            ? `${anuncioDe(sugestoes.length)}. ${String(sugestoes.length)} de ${String(total)} encontradas`
            : anuncioDe(sugestoes.length)
          : ''}
      </span>
    </div>
  )
}
